import React, { useState, useRef, useEffect } from 'react';
import productData from '../../data/product.json';
import {
  Container,
  VideoWrapper,
  Video,
  Controls,
  ProgressBarContainer,
  ProgressBar,
  BufferedBar,
  ViewDensityBar,
  ControlsRow,
  ControlsLeft,
  ControlsRight,
  ControlButton,
  TimeDisplay,
  VolumeControl,
  VolumeSlider,
  LoadingSpinner,
  SettingsButton,
  SettingsMenu,
  SettingsItem,
  Overlay,
  PlayPauseIcon,
  ProductBadge,
  SplitContainer,
  VideoSection,
  ProductsSection,
  ProductsTitle,
  ProductCard,
  ProductImage,
  ProductInfo,
  ProductTitle,
  ProductPrice,
  ProductLink,
  QRColumn,
  QRImage,
  Spinner,
  EmptyMessage,
} from './styles/embedded-video-player';

export default function EmbeddedVideoPlayer({ film }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showPlayPauseOverlay, setShowPlayPauseOverlay] = useState(false);
  const [viewDensityData, setViewDensityData] = useState([]);

  // New states for product feature
  const [currentFrame, setCurrentFrame] = useState(null);
  const [showProductBadge, setShowProductBadge] = useState(false);
  const [showProductsSidebar, setShowProductsSidebar] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState(null);

  // Generate mock view density data
  useEffect(() => {
    const generateMockData = () => {
      const points = 100;
      const data = [];

      for (let index = 0; index < points; index += 1) {
        const progress = duration > 0 ? (index / points) * duration : 0;
        const nearbyFrameCount = (film?.frames || []).filter(
          (frame) => Math.abs((frame.timestamp || 0) - progress) <= 10
        ).length;

        data.push(Math.min(25 + nearbyFrameCount * 20, 100));
      }

      setViewDensityData(data);

      const matchingFrame = (film?.frames || []).find(
        (frame) => Math.abs((frame.timestamp || 0) - currentTime) <= 2
      );

      if (matchingFrame) {
        setCurrentFrame(matchingFrame);
        setShowProductBadge(true);
      } else {
        setCurrentFrame(null);
        setShowProductBadge(false);
      }

      if (showProductsSidebar) {
        setShowProductBadge(false);
      }
    };

    generateMockData();
  }, [showProductsSidebar, currentTime, film?.frames, duration]);

  // Generate QR code from URL
  const generateQRCode = (url) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(url)}`;

  // Fetch products for current frame
  const fetchProductsForFrame = (frame) => {
    if (!frame?.timestamp) return;

    setLoadingProducts(true);
    setProductsError(null);

    try {
      // --- API call đã được comment lại, dùng local JSON thay thế ---
      // const apiUrl = process.env.API_GET_CLOTHES || 'https://vieon.farmcode.io.vn/analyze/url';
      // const response = await fetch(apiUrl, {
      //     method: 'POST',
      //     headers: { 'Content-Type': 'application/json' },
      //     body: JSON.stringify({ image_url: frame.thumbnail, k_per_item: 2 }),
      // });
      // if (!response.ok) throw new Error('Failed to fetch products');
      // const data = await response.json();

      // Lookup từ product.json theo video slug + timestamp
      const videoEntry = productData.find((entry) => entry.video === film.slug);
      const matchedProducts = videoEntry ? videoEntry.product.filter((p) => p.timestamp === frame.timestamp) : [];

      const extractedProducts = matchedProducts.map((p) => ({
        id: p.sku,
        sku: p.sku,
        title: p.title,
        category: p.category,
        color: p.color,
        price: p.price,
        image: p.image_url,
        product_url: p.product_url,
        slot: p.slot,
      }));

      setProducts(extractedProducts);
    } catch (err) {
      console.error('Error getting products:', err);
      setProductsError('Không thể tải sản phẩm');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle badge click
  const handleBadgeClick = (e) => {
    e.stopPropagation();

    // Exit fullscreen if active
    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }

    setShowProductsSidebar(true);
    if (currentFrame) {
      fetchProductsForFrame(currentFrame);
    }
  };

  // Auto exit fullscreen when showing products sidebar
  useEffect(() => {
    if (showProductsSidebar && isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  }, [showProductsSidebar, isFullscreen]);

  // Listen for fullscreen changes (ESC key, etc.)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);

      // If exiting fullscreen manually, close products sidebar
      if (!isCurrentlyFullscreen && showProductsSidebar) {
        setShowProductsSidebar(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [showProductsSidebar]);

  // Auto-hide controls after 3 seconds of inactivity
  useEffect(() => {
    const resetControlsTimeout = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (isPlaying) {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    };

    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / video.duration) * 100;
        setBuffered(bufferedPercent);
      }
    };

    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play();
      setIsPlaying(true);
    }

    setShowPlayPauseOverlay(true);
    setTimeout(() => setShowPlayPauseOverlay(false), 500);
  };

  const skip = (seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * video.duration;
  };

  const changeVolume = (delta) => {
    const newVolume = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    // Close products sidebar if entering fullscreen
    if (!isFullscreen && showProductsSidebar) {
      setShowProductsSidebar(false);
    }

    if (!isFullscreen) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const changePlaybackRate = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
      setShowSettings(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || Number.isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getVolumeEmoji = () => {
    if (isMuted || volume === 0) return '🔇';
    if (volume < 0.5) return '🔉';
    return '🔊';
  };

  return (
    <SplitContainer showSidebar={showProductsSidebar}>
      <VideoSection showSidebar={showProductsSidebar}>
        <Container ref={containerRef} onMouseMove={() => setShowControls(true)}>
          <VideoWrapper showControls={showControls} onClick={togglePlayPause}>
            <Video ref={videoRef} src={film.videoUrl} />

            {/* Product Badge */}
            {showProductBadge && !showProductsSidebar && !isFullscreen && (
              <ProductBadge onClick={handleBadgeClick}>🛍️ Xem chi tiết sản phẩm</ProductBadge>
            )}

            {isLoading && (
              <LoadingSpinner>
                <div className="spinner" />
              </LoadingSpinner>
            )}

            {showPlayPauseOverlay && (
              <Overlay>
                <PlayPauseIcon>{isPlaying ? '▶' : '⏸'}</PlayPauseIcon>
              </Overlay>
            )}

            <Controls show={showControls}>
              <ProgressBarContainer onClick={handleProgressClick}>
                {/* View Density Bars */}
                {viewDensityData.map((height, index) => (
                  <ViewDensityBar key={index} height={height} position={(index / viewDensityData.length) * 100} />
                ))}

                <BufferedBar buffered={buffered} />
                <ProgressBar progress={(currentTime / duration) * 100} />
              </ProgressBarContainer>

              <ControlsRow>
                <ControlsLeft>
                  <ControlButton
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlayPause();
                    }}
                  >
                    {isPlaying ? '⏸' : '▶'}
                  </ControlButton>
                  <ControlButton
                    onClick={(e) => {
                      e.stopPropagation();
                      skip(-10);
                    }}
                  >
                    ⏪
                  </ControlButton>
                  <ControlButton
                    onClick={(e) => {
                      e.stopPropagation();
                      skip(10);
                    }}
                  >
                    ⏩
                  </ControlButton>

                  <VolumeControl>
                    <ControlButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMute();
                      }}
                    >
                      {getVolumeEmoji()}
                    </ControlButton>
                    <VolumeSlider
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={isMuted ? 0 : volume}
                      onChange={(e) => {
                        e.stopPropagation();
                        changeVolume(parseFloat(e.target.value) - volume);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </VolumeControl>

                  <TimeDisplay>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </TimeDisplay>
                </ControlsLeft>

                <ControlsRight>
                  <SettingsButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSettings(!showSettings);
                    }}
                  >
                    ⚙️
                  </SettingsButton>
                  {showSettings && (
                    <SettingsMenu onClick={(e) => e.stopPropagation()}>
                      {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                        <SettingsItem
                          key={rate}
                          active={playbackRate === rate}
                          onClick={() => changePlaybackRate(rate)}
                        >
                          {rate}x {playbackRate === rate && '✓'}
                        </SettingsItem>
                      ))}
                    </SettingsMenu>
                  )}
                  <ControlButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFullscreen();
                    }}
                  >
                    {isFullscreen ? '⛶' : '⛶'}
                  </ControlButton>
                </ControlsRight>
              </ControlsRow>
            </Controls>
          </VideoWrapper>
        </Container>
      </VideoSection>

      {/* Products Sidebar */}
      {showProductsSidebar && (
        <ProductsSection>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <ProductsTitle>Sản phẩm trong khung hình</ProductsTitle>
            <ControlButton onClick={() => setShowProductsSidebar(false)}>✕</ControlButton>
          </div>

          {loadingProducts && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spinner />
              <div style={{ color: 'white', marginTop: '15px', fontSize: '14px' }}>Đang phân tích trang phục...</div>
            </div>
          )}

          {productsError && <EmptyMessage style={{ color: '#ff6b6b' }}>{productsError}</EmptyMessage>}

          {!loadingProducts && !productsError && products.length === 0 && (
            <EmptyMessage>Không tìm thấy sản phẩm phù hợp</EmptyMessage>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}
          >
            {!loadingProducts &&
              products.map((product) => (
                <ProductCard key={product.id}>
                  <ProductImage src={product.image} alt={product.title} />
                  <ProductInfo>
                    <ProductTitle>{product.title}</ProductTitle>
                    <ProductPrice>
                      {new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                      }).format(product.price)}
                    </ProductPrice>
                    <ProductLink href={product.product_url} target="_blank" rel="noopener noreferrer">
                      Xem sản phẩm
                    </ProductLink>
                  </ProductInfo>
                  <QRColumn>
                    <QRImage src={generateQRCode(product.product_url)} alt="QR Code" />
                  </QRColumn>
                </ProductCard>
              ))}
          </div>
        </ProductsSection>
      )}
    </SplitContainer>
  );
}
